/**
 * Simulator-vyn, Lager 2.
 *
 * Layout:
 *   - På breda skärmar: vänster kolumn för sammanställning och graf,
 *     höger kolumn för kontroller (batteri + förutsättningar).
 *   - På smala skärmar: allt stackat vertikalt med kontroller efter graf.
 *
 * Roadmap:
 *   Lager 1: månadsgraf utan batteri (klart)
 *   Lager 2 (denna): batterikonfiguration + jämförelse (klart)
 *   Lager 3: dekomposition av besparingen
 *   Lager 4: storlek × smarthet med optimum
 *   Lager 5: omvänd kalkyl
 */

import { useEffect, useMemo, useState } from "react";
import type { UserData } from "../types";
import { describeSource } from "../types";
import MonthlyCostChart from "../components/MonthlyCostChart";
import MonthlySavingsChart from "../components/MonthlySavingsChart";
import AssumptionsPanel from "../components/AssumptionsPanel";
import ProfileTuningPanel from "../components/ProfileTuningPanel";
import ShareLinkButton from "../components/ShareLinkButton";
import WeakResultPanel from "../components/WeakResultPanel";
import InvestmentPlansPanel from "../components/InvestmentPlansPanel";
import WinterInsightCard from "../components/WinterInsightCard";
import UsageTimingPanel from "../components/UsageTimingPanel";
import { useAnalytics } from "../hooks/useAnalytics";
import { bucketInvestmentSek } from "../types/analytics";
import { calculateTimingAnalysis, calculateHourlyProfile } from "../sim/usage-timing";
import BatteryControls from "../components/BatteryControls";
import DecompositionPanel from "../components/DecompositionPanel";
import SoftValues from "../components/SoftValues";
import CollapsibleSection from "../components/CollapsibleSection";
import { useSimulation } from "../hooks/useSimulation";
import { DEFAULT_TAX_2026, decomposeSavings, importPricePerKWh } from "../sim/costs";
import { applyInvestmentPlansToHours, calculateImportContext } from "../sim/investment-plans";
import { GENERIC_SE, GRID_FEE_PRESETS } from "../data/gridFees";
import GridOperatorSelector from "../components/GridOperatorSelector";
import type { HourPoint, Scenario } from "../sim/types";
import { DEFAULT_DEGRADATION } from "../sim/types";

interface SimulatorProps {
  userData: UserData;
  onLogout: () => void;
  onUserDataUpdated?: (data: UserData) => void;
  initialScenarioOverride?: Partial<Scenario>;
}

function filterCompleteMonths(hours: HourPoint[]): {
  hours: HourPoint[];
  monthCount: number;
} {
  const byMonth = new Map<string, HourPoint[]>();
  for (const h of hours) {
    const d = new Date(h.from);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const existing = byMonth.get(key);
    if (existing) existing.push(h);
    else byMonth.set(key, [h]);
  }

  const complete: HourPoint[] = [];
  let monthCount = 0;
  for (const [key, monthHours] of byMonth) {
    const [yearStr, monthStr] = key.split("-");
    const year = parseInt(yearStr ?? "0", 10);
    const month = parseInt(monthStr ?? "0", 10);
    const daysInMonth = new Date(year, month, 0).getDate();
    if (monthHours.length >= daysInMonth * 24 * 0.95) {
      complete.push(...monthHours);
      monthCount++;
    }
  }
  return { hours: complete, monthCount };
}

function formatKr(v: number): string {
  return new Intl.NumberFormat("sv-SE", {
    maximumFractionDigits: 0,
  }).format(Math.round(v));
}

/**
 * Klassificera återbetalningstid mot förväntad batterilivslängd.
 *   good     = payback <  60% av livslängd, gott om marginal
 *   warning  = payback 60-90% av livslängd, snäv marginal
 *   critical = payback >= 90% av livslängd, hinner inte betala sig
 */
type PaybackStatus = "good" | "warning" | "critical";

function classifyPayback(
  paybackYears: number | undefined,
  lifetimeYears: number,
): PaybackStatus | null {
  if (paybackYears === undefined || paybackYears >= 100) return null;
  const ratio = paybackYears / lifetimeYears;
  if (ratio >= 0.9) return "critical";
  if (ratio >= 0.6) return "warning";
  return "good";
}

/**
 * Räkna fram total och genomsnittlig årsbesparing över batteriets livslängd
 * med hänsyn till kapacitetsdegradering. Antagandet: besparing skalar
 * proportionellt mot kapacitet, vilket är en god approximation.
 *
 * Linjär degradering: kapacitet(y) = 1 - r*y där y räknas från 0.
 * Genomsnittlig faktor över Y år = integral / Y = 1 - r*Y/2.
 * Total = annualSavings * Y * (1 - r*Y/2).
 */
function calculateLifetimeSavings(
  annualSavings: number,
  lifetimeYears: number,
  annualLossRate: number,
): { totalSek: number; averagePerYear: number; ratioVsYear1: number } {
  const avgFactor = Math.max(0, 1 - (annualLossRate * lifetimeYears) / 2);
  return {
    totalSek: annualSavings * lifetimeYears * avgFactor,
    averagePerYear: annualSavings * avgFactor,
    ratioVsYear1: avgFactor,
  };
}

const DEFAULT_SCENARIO: Scenario = {
  battery: {
    capacityKWh: 7,
    maxPowerKW: 6,
    roundTripEfficiency: 0.9,
    minSocFraction: 0.05,
    // 50 000 kr för 7 kWh installerat. Sweet spot för svenska villor
    // utan stor sol — marginalnyttan över 7-8 kWh minskar kraftigt och
    // priset blir oftast mer fördelaktigt per kr investerad krona.
    investmentSek: 50_000,
  },
  strategy: "predictive-24h",
  ancillary: {
    // Av som default. När batteriets payback ser dålig ut visar appen
    // en pedagogisk panel som låter användaren själv slå på stödtjänster
    // och se vad det gör med besparingen. Att slå på det per default
    // skulle dölja vad som faktiskt rör nålen — den första interaktionen
    // är där poletten faller ned.
    enabled: false,
    annualRevenuePerKW: 1500,
    aggregatorShare: 0.25,
    annualPriceMultiplier: 0.85,
    horizonYears: 5,
    reservedCapacityFraction: 0.25,
  },
  grid: GENERIC_SE.tariff,
  tax: DEFAULT_TAX_2026,
  supplier: {
    importMarkupPerKWh: 0.06,
    exportMarkdownPerKWh: 0,
    exportBonusPerKWh: 0,
  },
  // Neutral default — verkliga LFP-batterier 2026 levererar typiskt
  // 10-15 års funktionell livslängd med 1.5-2.5% kapacitetstapp/år
  degradation: {
    expectedLifetimeYears: 12,
    annualCapacityLossRate: 0.02,
  },
  investmentPlans: {
    enabled: false,
    plans: [],
  },
};

/**
 * Visas när återbetalningstiden är mycket lång (>=90% av livslängd). Ger
 * konkreta förslag på vad som skulle kunna göra investeringen mer aktuell:
 * andra scenarios att modellera, alternativa investeringar, marknadsfaktorer.
 */
function PoorRoiSuggestions({
  ancillaryEnabled,
  powerTariffEnabled,
}: {
  ancillaryEnabled: boolean;
  powerTariffEnabled: boolean;
}) {
  return (
    <div className="poor-roi-suggestions">
      <h4>Lång återbetalning. Överväg dessa</h4>
      <ul>
        {!ancillaryEnabled && (
          <li>
            <strong>Stödtjänster (FCR-D)</strong> är inte modellerade. Slå
            på toggle under Förutsättningar. Det kan ge 1000-2000 kr/år extra
            intäkt via aggregator som CheckWatt eller Tibber.
          </li>
        )}
        {!powerTariffEnabled && (
          <li>
            <strong>Effekttariff</strong> är inte modellerad. Många nätbolag
            planerar att införa den under 2026-2028. Slå på scenariot för
            att se vad det skulle innebära.
          </li>
        )}
        <li>
          <strong>ROT-avdrag</strong> 30% av arbetskostnaden räknas inte in
          här. För en typisk 70 000 kr installation kan det vara värt
          5000-8000 kr. Räkna av det från totalpriset.
        </li>
        <li>
          <strong>Vänta på prisutveckling</strong>. Batteripriser sjunker
          typiskt 5-10% per år i Sverige. Att vänta 1-2 år kan göra
          investeringen 10-20% billigare.
        </li>
        <li>
          <strong>Smart värmepump som alternativ</strong>. Termiskt lager
          (varmvattentank, golvvärme) med prisstyrning ger ofta bättre ROI
          än batteri för samma kapital, särskilt vid stora värmebehov.
        </li>
        <li>
          <strong>Mindre batteri</strong>. Marginalnyttan minskar snabbt.
          Om payback är dålig på 10 kWh kan 5 kWh ofta vara mer lönsamt per
          investerad krona.
        </li>
      </ul>
    </div>
  );
}

export default function Simulator({ userData, onLogout, onUserDataUpdated, initialScenarioOverride }: SimulatorProps) {
  const { metadata, hours } = userData;
  const home = metadata.tibberHome;

  const { hours: completeHours, monthCount } = useMemo(
    () => filterCompleteMonths(hours),
    [hours],
  );

  const isMobile =
    typeof window !== "undefined" && window.innerWidth < 720;

  const [scenario, setScenario] = useState<Scenario>(() => ({
    ...DEFAULT_SCENARIO,
    ...(initialScenarioOverride ?? {}),
  }));

  // Debounce scenario så vi inte kör DP-simuleringen vid varje sliderändring.
  // Reglagen rör sig fortfarande direkt, men simuleringen kör först när
  // användaren har slutat röra slidern i 250ms.
  const [debouncedScenario, setDebouncedScenario] = useState(scenario);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedScenario(scenario);
    }, 250);
    return () => clearTimeout(timer);
  }, [scenario]);

  // Modifiera timdata baserat på planerade investeringar (isolering, kamin, elbil)
  // Sker INNAN simulering så batteriet räknar på den nya förbrukningen.
  const modifiedHours = useMemo(() => {
    if (!debouncedScenario.investmentPlans) return completeHours;
    return applyInvestmentPlansToHours(
      completeHours,
      debouncedScenario.investmentPlans,
    );
  }, [completeHours, debouncedScenario.investmentPlans]);

  // Kör simuleringen i en Web Worker så UI förblir responsivt även under
  // tunga DP-pass. Worker återanvänds över komponentens livstid.
  const { result, isComputing } = useSimulation(
    modifiedHours,
    debouncedScenario,
    modifiedHours.length >= 24,
  );

  // Pending = debounce väntar ELLER workern räknar. Används för att indikera
  // att resultatet inte är aktuellt ännu.
  const isPending = scenario !== debouncedScenario || isComputing;

  const breakdown = useMemo(() => {
    if (!result) return null;
    return decomposeSavings(
      modifiedHours,
      result.hours,
      debouncedScenario,
      monthCount,
    );
  }, [modifiedHours, result, debouncedScenario, monthCount]);

  // Genomsnittligt importpris (kr/kWh) för payback-beräkning på
  // investeringsplaner. Viktat efter import per timme för att spegla
  // verklig kostnad bättre än oviktat snitt.
  const avgImportPricePerKWh = useMemo(() => {
    if (completeHours.length === 0) return 3;
    const annualImport =
      completeHours.reduce((s, h) => s + h.importKWh, 0) *
      (12 / Math.max(1, monthCount));
    let weightedSum = 0;
    let totalImport = 0;
    for (const h of completeHours) {
      const price = importPricePerKWh(
        h,
        scenario.tax,
        scenario.supplier,
        scenario.grid,
        annualImport,
      );
      weightedSum += price * h.importKWh;
      totalImport += h.importKWh;
    }
    return totalImport > 0 ? weightedSum / totalImport : 3;
  }, [completeHours, monthCount, scenario.tax, scenario.supplier, scenario.grid]);

  // Importkontext för dynamiska investeringsplan-presets. Skalas till
  // 12 månader så preset-spann blir korrekta även om vi har partial data.
  const importContext = useMemo(() => {
    const raw = calculateImportContext(completeHours);
    const scale = 12 / Math.max(1, monthCount);
    return {
      annualImportKWh: raw.annualImportKWh * scale,
      winterImportKWh: raw.winterImportKWh * scale,
      eveningImportKWh: raw.eveningImportKWh * scale,
    };
  }, [completeHours, monthCount]);

  // Analytics: anonymiserad datainsamling för plattformsutveckling.
  // tibberHomeId hashas serverside till pseudonym — klienten kan aldrig
  // återskapa den.
  const analytics = useAnalytics(home?.id ?? null);

  // Profile-event en gång per session när data finns
  useEffect(() => {
    if (completeHours.length < 24 || !home) return;
    const monthlyImports: number[] = Array.from({ length: 12 }, () => 0);
    // 12×24-matris för månadsspecifik dygnsfördelning
    const monthlyHourlySums: number[][] = Array.from({ length: 12 }, () =>
      Array.from({ length: 24 }, () => 0),
    );
    let annualImport = 0;
    let annualExport = 0;
    for (const h of completeHours) {
      const d = new Date(h.from);
      const month = d.getMonth();
      const hour = d.getHours();
      monthlyImports[month]! += h.importKWh;
      monthlyHourlySums[month]![hour]! += h.importKWh;
      annualImport += h.importKWh;
      annualExport += h.exportKWh;
    }
    // Skala till 12 månader för representativitet
    const scale = 12 / Math.max(1, monthCount);
    const scaledMonthly = monthlyImports.map((v) => Math.round(v * scale));
    // Normalisera per månad så summa = 1.0. Behåller scale-invarians —
    // utan beroende av faktiska kWh-volymer kan vi återanvända shape:n
    // på vilken årsförbrukning som helst i wizardsyntesen.
    const monthlyHourlyShape: number[][] = monthlyHourlySums.map((hours) => {
      const sum = hours.reduce((s, v) => s + v, 0);
      if (sum <= 0) return hours.map(() => 0);
      return hours.map((v) => Math.round((v / sum) * 10000) / 10000);
    });

    // Härled analytics-dataSource från app-nivåns DataSource. Tibber-flöden
    // ger "tibber" (pålitlig + cross-session-aggregerbar). CSV-uppladdning
    // ger "csv_upload" (också pålitlig). Wizard-gissningar kommer ge "wizard"
    // när den vägen byggs. Backend filtrerar wizard bort från typprofil-banken.
    const dataSource: "tibber" | "csv_upload" | "wizard" =
      metadata.source.startsWith("tibber")
        ? "tibber"
        : metadata.source.endsWith("-csv")
          ? "csv_upload"
          : "wizard";

    // Härled postalCodePrefix från antingen wizardinput eller Tibber-adress.
    // Aldrig fullt postnummer — bara 2-siffrigt prefix (länsnivå-grov).
    const fullPostalCode =
      metadata.wizardInput?.postalCodePrefix ?? home.address.postalCode ?? "";
    const postalCodePrefix = fullPostalCode.replace(/\D/g, "").slice(0, 2);

    analytics.trackProfile({
      dataSource,
      priceZone: home.priceArea ?? "unknown",
      fuseSizeAmp: scenario.grid.fuseSize ?? 16,
      annualImportKWh: Math.round(annualImport * scale),
      annualExportKWh: Math.round(annualExport * scale),
      monthlyImportsKWh: scaledMonthly,
      monthlyHourlyShape,
      monthCount,
      ...(postalCodePrefix.length === 2 && { postalCodePrefix }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completeHours.length, home?.id]);

  // Scenario-event vid varje konvergerad simulering
  useEffect(() => {
    if (!result || !breakdown) return;
    // Räkna timing-score från completeHours (utan investeringsplaner)
    const timingScore = calculateTimingAnalysis(completeHours, monthCount).score;
    const timingHourly = calculateHourlyProfile(completeHours);
    const totalImport = timingHourly.reduce((s, p) => s + p.avgImportKWh, 0);
    const winterTotal = timingHourly.reduce((s, p) => s + p.avgImportKWh, 0); // approximation

    // Härled livslängd-preset (matchar AssumptionsPanel)
    const lifetime = debouncedScenario.degradation?.expectedLifetimeYears ?? 12;
    const lifetimePreset =
      lifetime <= 10 ? "conservative" : lifetime >= 15 ? "optimistic" : "neutral";

    const investmentPlanTypes =
      debouncedScenario.investmentPlans?.enabled
        ? debouncedScenario.investmentPlans.plans
            .filter((p) => p.enabled)
            .map((p) => p.type)
        : [];

    const paybackYears =
      result.annual.savingsSek > 0
        ? (debouncedScenario.battery.investmentSek ?? 0) / result.annual.savingsSek
        : 999;

    const totalLifetimeSavings = result.annual.savingsSek * lifetime;
    const investmentSek = debouncedScenario.battery.investmentSek ?? 0;
    const totalRoi =
      investmentSek > 0
        ? ((totalLifetimeSavings - investmentSek) / investmentSek) * 100
        : 0;

    // Detekterar om vinterinsikten visas just nu (samma logik som komponenten)
    let winterImport = 0;
    let totalImportAbs = 0;
    let winterReduction = 0;
    let totalReduction = 0;
    const resultMap = new Map(result.hours.map((r) => [r.from, r]));
    for (const h of modifiedHours) {
      const month = new Date(h.from).getMonth();
      const isWinter = month === 11 || month === 0 || month === 1;
      totalImportAbs += h.importKWh;
      if (isWinter) winterImport += h.importKWh;
      const r = resultMap.get(h.from);
      if (r) {
        totalReduction += h.importKWh - r.importKWh;
        if (isWinter) winterReduction += h.importKWh - r.importKWh;
      }
    }
    const winterShare = totalImportAbs > 0 ? winterImport / totalImportAbs : 0;
    const winterReductionShare =
      totalReduction > 0 ? winterReduction / totalReduction : 0;
    const sawWinterInsight =
      winterShare > 0.35 &&
      winterReductionShare < 0.25 &&
      result.annual.savingsSek > 0;

    analytics.trackScenario({
      batteryCapacityKWh: debouncedScenario.battery.capacityKWh,
      batteryMaxPowerKW: debouncedScenario.battery.maxPowerKW,
      batteryInvestmentSekBucket: bucketInvestmentSek(investmentSek),
      strategy: debouncedScenario.strategy,
      ancillaryEnabled: debouncedScenario.ancillary.enabled,
      powerTariffEnabled: !!debouncedScenario.grid.powerTariff,
      lifetimePreset,
      investmentPlanTypes,
      annualSavingsSek: Math.round(result.annual.savingsSek),
      paybackYears: Math.round(paybackYears * 10) / 10,
      totalLifetimeSavingsSek: Math.round(totalLifetimeSavings),
      totalRoiPercent: Math.round(totalRoi),
      timingScore: Math.round(timingScore),
      sawWinterInsight,
    });

    // Tracka om investeringsplaner aktiverats
    if (investmentPlanTypes.length > 0) {
      analytics.markEngagement("addedInvestmentPlans");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, breakdown, debouncedScenario]);

  function scrollTo(id: string) {
    const el = document.getElementById(id);
    if (!el) return;
    const stickyHeight = 96; // ungefär höjden av sticky bar
    const y = el.getBoundingClientRect().top + window.scrollY - stickyHeight;
    window.scrollTo({ top: y, behavior: "smooth" });
  }

  return (
    <section className="simulator">
      {result && (
        <div className={`sticky-summary ${isPending ? "is-pending" : ""}`}>
          <div className="sticky-summary-primary">
            <div className="sticky-summary-item">
              <span className="sticky-summary-label">Besparing</span>
              <strong className="sticky-summary-value">
                {formatKr(result.annual.savingsSek)}&nbsp;kr/år
              </strong>
            </div>
            {result.paybackYears !== undefined &&
              result.paybackYears < 100 && (
                <div className="sticky-summary-item">
                  <span className="sticky-summary-label">Återbetalning</span>
                  <strong
                    className={`sticky-summary-value payback-${
                      classifyPayback(
                        result.paybackYears,
                        (scenario.degradation ?? DEFAULT_DEGRADATION)
                          .expectedLifetimeYears,
                      ) ?? "good"
                    }`}
                  >
                    {result.paybackYears.toFixed(1)}&nbsp;år
                  </strong>
                  <span className="sticky-summary-sublabel">
                    av{" "}
                    {(scenario.degradation ?? DEFAULT_DEGRADATION)
                      .expectedLifetimeYears}{" "}
                    års livslängd
                  </span>
                </div>
              )}
            {scenario.battery.investmentSek !== undefined &&
              scenario.battery.investmentSek > 0 && (
                <div className="sticky-summary-item">
                  <span className="sticky-summary-label">Investering</span>
                  <strong className="sticky-summary-value">
                    {formatKr(scenario.battery.investmentSek)}&nbsp;kr
                  </strong>
                </div>
              )}
          </div>
          <nav className="sticky-summary-nav">
            <button type="button" onClick={() => scrollTo("section-controls")}>
              Inställningar
            </button>
            <button type="button" onClick={() => scrollTo("section-result")}>
              Resultat
            </button>
            <button type="button" onClick={() => scrollTo("section-details")}>
              Detaljer
            </button>
            <button type="button" onClick={() => scrollTo("section-values")}>
              Värden
            </button>
          </nav>
        </div>
      )}

      <header className="simulator-header">
        <h2>Ditt kommande år med batteri</h2>
        <p className="hint">
          {home?.nickname && <strong>{home.nickname} · </strong>}
          {home?.priceArea}
          {home?.address.city ? ` · ${home.address.city}` : ""}
        </p>
        <p className="metadata-banner">
          <span className="metadata-pill">
            {describeSource(metadata.source)}
          </span>
          <span>
            {metadata.quality === "modeled"
              ? "Syntetisk årsprofil"
              : `${monthCount} hela månader som mall`}
          </span>
        </p>
        <p className="forward-looking-note">
          {metadata.quality === "modeled"
            ? "Profilen är beräknad utifrån dina svar och typvärden för svenska hushåll. Resultatet är ungefärligt och kan avvika omkring 20 procent mot ditt verkliga utfall. Skatter och tariffer räknas enligt aktuell lagstiftning för 2026 framåt."
            : "Modellen projicerar kommande år baserat på dina senaste 12 månader. Skatter och tariffer räknas enligt aktuell lagstiftning för 2026 framåt. 60-öringen ingår inte (upphörde 2025-12-31)."}
        </p>
      </header>

      <div className="simulator-layout">
        <div className="simulator-main">
          {result && (
            <div
              className={`savings-summary ${isPending ? "is-pending" : ""}`}
              id="section-result"
            >
              <div className="savings-headline">
                <div className="savings-figure">
                  <span className="savings-amount">
                    {formatKr(result.annual.savingsSek)} kr
                  </span>
                  <span className="savings-label">besparing per år</span>
                </div>
                {result.paybackYears !== undefined && (
                  <div className="savings-figure">
                    <span
                      className={`savings-amount payback-${
                        classifyPayback(
                          result.paybackYears,
                          (scenario.degradation ?? DEFAULT_DEGRADATION)
                            .expectedLifetimeYears,
                        ) ?? "good"
                      }`}
                    >
                      {result.paybackYears < 100
                        ? result.paybackYears.toFixed(1)
                        : "∞"}{" "}
                      år
                    </span>
                    <span className="savings-label">
                      återbetalning vid{" "}
                      {formatKr(scenario.battery.investmentSek ?? 0)} kr
                    </span>
                    <span className="savings-sublabel">
                      Förväntad livslängd:{" "}
                      {
                        (scenario.degradation ?? DEFAULT_DEGRADATION)
                          .expectedLifetimeYears
                      }{" "}
                      år
                      {result.paybackYears < 100 &&
                        result.paybackYears >=
                          (scenario.degradation ?? DEFAULT_DEGRADATION)
                            .expectedLifetimeYears *
                            0.9 &&
                        " · hinner inte betala sig innan kapaciteten degraderar"}
                    </span>
                  </div>
                )}
                <div className="savings-figure">
                  <span className="savings-amount">
                    {Math.round(result.annual.selfConsumptionUplift * 100)}%
                  </span>
                  <span className="savings-label">
                    minskning av export (mer sol-egenanvändning)
                  </span>
                </div>
              </div>
              {(() => {
                const deg = scenario.degradation ?? DEFAULT_DEGRADATION;
                const life = calculateLifetimeSavings(
                  result.annual.savingsSek,
                  deg.expectedLifetimeYears,
                  deg.annualCapacityLossRate,
                );
                const investment = scenario.battery.investmentSek ?? 0;
                const netLifetime = life.totalSek - investment;
                const isPositive = netLifetime > 0;
                const totalRoi = investment > 0 ? netLifetime / investment : 0;
                const annualRoi = totalRoi / deg.expectedLifetimeYears;
                const roiClass = isPositive
                  ? "payback-good"
                  : totalRoi > -0.2
                  ? "payback-warning"
                  : "payback-critical";
                return (
                  <div className="lifetime-summary">
                    <div className="lifetime-row">
                      <span className="lifetime-label">
                        Total besparing över {deg.expectedLifetimeYears} år
                      </span>
                      <strong className="lifetime-value">
                        {formatKr(life.totalSek)} kr
                      </strong>
                    </div>
                    <div className="lifetime-row">
                      <span className="lifetime-label">
                        Genomsnitt per år (efter degradering)
                      </span>
                      <strong className="lifetime-value">
                        {formatKr(life.averagePerYear)} kr
                      </strong>
                    </div>
                    {investment > 0 && (
                      <>
                        <div className="lifetime-row lifetime-net">
                          <span className="lifetime-label">
                            Netto efter investering ({formatKr(investment)} kr)
                          </span>
                          <strong className={`lifetime-value ${roiClass}`}>
                            {isPositive ? "+" : ""}
                            {formatKr(netLifetime)} kr
                          </strong>
                        </div>
                        <div className="lifetime-row">
                          <span className="lifetime-label">
                            Avkastning på investerat kapital
                          </span>
                          <strong className={`lifetime-value ${roiClass}`}>
                            {totalRoi >= 0 ? "+" : ""}
                            {(totalRoi * 100).toFixed(0)}% totalt ·{" "}
                            {annualRoi >= 0 ? "+" : ""}
                            {(annualRoi * 100).toFixed(1)}%/år
                          </strong>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
              {result.paybackYears !== undefined &&
                (() => {
                  const lifetimeYears = (
                    scenario.degradation ?? DEFAULT_DEGRADATION
                  ).expectedLifetimeYears;
                  // Visa pedagogisk panel vid svaga resultat. Trösklar:
                  //   - nettobesparing < 2000 kr/år (kvalitativt svagt)
                  //   - eller payback > 60% av batteriets livslängd
                  //   - eller besparing < 0 (kalkylen går minus)
                  const isWeak =
                    result.annual.savingsSek < 2000 ||
                    result.paybackYears > lifetimeYears * 0.6;
                  if (!isWeak) return null;
                  return (
                    <WeakResultPanel
                      netSavingsSek={result.annual.savingsSek}
                      paybackYears={result.paybackYears}
                      lifetimeYears={lifetimeYears}
                      scenario={scenario}
                      onScenarioChange={setScenario}
                    />
                  );
                })()}
              <div className="savings-breakdown">
                <span>
                  Utan batteri:{" "}
                  <strong>{formatKr(result.annual.baselineCostSek)} kr/år</strong>
                </span>
                <span>→</span>
                <span>
                  Med batteri:{" "}
                  <strong>{formatKr(result.annual.batteryCostSek)} kr/år</strong>
                </span>
                <span>
                  · {result.annual.fullCycles.toFixed(0)} fullcykler/år
                </span>
              </div>
            </div>
          )}

          <h3>Elkostnad månad för månad</h3>
          <p className="hint">
            Två linjer: ljusare visar din nuvarande nettokostnad, mörkare
            visar vad du betalar med batteri. Skillnaden mellan dem är
            besparingen den månaden.
          </p>

          {result && (
            <MonthlyCostChart
              baselineHours={modifiedHours}
              batteryHours={result.hours}
              scenario={debouncedScenario}
            />
          )}

          {result && (
            <MonthlySavingsChart
              baselineHours={modifiedHours}
              batteryHours={result.hours}
              scenario={debouncedScenario}
            />
          )}

          {breakdown && (
            <CollapsibleSection
              id="section-details"
              title="Var kommer besparingen ifrån?"
              summary={`Detaljerad uppdelning av ${formatKr(
                result?.annual.savingsSek ?? 0,
              )} kr/år`}
              defaultOpen={!isMobile}
            >
              <DecompositionPanel breakdown={breakdown} />
            </CollapsibleSection>
          )}

          {metadata.quality !== "modeled" && (
            <CollapsibleSection
              id="section-timing"
              title="Hur bra timear du redan?"
              summary="Din nuvarande skicklighet i att förbruka när priset är lågt"
              defaultOpen={false}
            >
              <UsageTimingPanel hours={modifiedHours} monthCount={monthCount} />
            </CollapsibleSection>
          )}

          {result && (
            <WinterInsightCard
              baselineHours={modifiedHours}
              resultHours={result.hours}
              scenario={scenario}
              onScrollToInvestments={() => scrollTo("section-investments")}
            />
          )}

          <CollapsibleSection
            id="section-values"
            title="Mer än kronor"
            summary="Försäkring, autonomi, miljö"
            defaultOpen={!isMobile}
          >
            <SoftValues />
          </CollapsibleSection>
        </div>

        <aside className="simulator-sidebar" id="section-controls">
          {metadata.quality === "modeled" && metadata.wizardInput && onUserDataUpdated && (
            <ProfileTuningPanel
              userData={userData}
              onUpdated={onUserDataUpdated}
            />
          )}
          <BatteryControls
            battery={scenario.battery}
            strategy={scenario.strategy}
            fuseSize={scenario.grid.fuseSize}
            onBatteryChange={(battery) =>
              setScenario({ ...scenario, battery })
            }
            onStrategyChange={(strategy) =>
              setScenario({ ...scenario, strategy })
            }
          />
          <GridOperatorSelector
            selectedId={
              GRID_FEE_PRESETS.find(
                (p) =>
                  p.tariff.operator === scenario.grid.operator &&
                  p.fuseSize === scenario.grid.fuseSize,
              )?.id ?? GENERIC_SE.id
            }
            onChange={(preset) =>
              setScenario({ ...scenario, grid: preset.tariff })
            }
          />
          <AssumptionsPanel scenario={scenario} onChange={setScenario} />
          <div id="section-investments">
            <InvestmentPlansPanel
              plans={
                scenario.investmentPlans ?? { enabled: false, plans: [] }
              }
              onChange={(investmentPlans) =>
                setScenario({ ...scenario, investmentPlans })
              }
              avgImportPricePerKWh={avgImportPricePerKWh}
              importContext={importContext}
            />
          </div>
        </aside>
      </div>

      {/* Dela-knappen fungerar för alla datakällor: wizard delar inline i
          URL:en, Tibber/CSV skapar en server-snapshot. Visas när det finns
          ett resultat att dela. */}
      {result && (
        <ShareLinkButton userData={userData} scenario={scenario} />
      )}

      <footer className="simulator-footer">
        <div className="simulator-footer-meta">
          <p className="privacy-notice">
            För att förbättra modellen samlas anonymiserad data in om
            beräkningar (batteristorlek, prisområde, förbrukningsprofil i
            aggregerad form). Ingen e-post, ingen identifierbar information.
            Tibber home-id pseudonymiseras serverside med hemlig nyckel.
          </p>
          <p className="simulator-footer-contact">
            Kontakta oss på{" "}
            <a href="mailto:hej@batterikalkyl.se" className="link">
              hej@batterikalkyl.se
            </a>{" "}
            · Byggd av{" "}
            <a
              href="https://vibeorg.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="link"
            >
              Vibeorg
            </a>
          </p>
        </div>
        <button type="button" className="link" onClick={onLogout}>
          {metadata.source.startsWith("tibber") ? "Logga ut" : "Börja om"}
        </button>
      </footer>
    </section>
  );
}
